import type { EntityId } from './ids.js';
import type { PrivacyClass } from './privacy.js';
import type { RecordEnvelope } from './records.js';

/**
 * Work & Content — the content production pipeline.
 *
 * A ContentItem flows idea → draft → review → scheduled → published. It links
 * to the project/learning/insight it came from, so public signal grows from
 * real proof (per the docs) rather than spam. No blind auto-posting, no vanity
 * cadence — publishing is an explicit owner step.
 */

export type ContentStage = 'idea' | 'draft' | 'review' | 'scheduled' | 'published';
export const CONTENT_STAGES: readonly ContentStage[] = ['idea', 'draft', 'review', 'scheduled', 'published'] as const;

export interface ContentItem extends RecordEnvelope {
  readonly kind: 'content_item';
  readonly title: string;
  /** Free-form platform label (youtube, blog, linkedin, x, …). */
  readonly platform: string;
  readonly stage: ContentStage;
  readonly note: string;
  readonly url: string;
  /** Linked project/skill/note this content is proof of (ids only). */
  readonly linkedIds: readonly EntityId[];
}

export interface ContentItemInput {
  readonly title: string;
  readonly platform?: string;
  readonly note?: string;
  readonly url?: string;
  readonly privacyClass?: PrivacyClass;
}

export interface ContentItemPatch {
  readonly title?: string;
  readonly platform?: string;
  readonly note?: string;
  readonly url?: string;
}

export function isPublished(stage: ContentStage): boolean {
  return stage === 'published';
}

export interface ContentSummary {
  readonly total: number;
  readonly published: number;
  readonly inProgress: number;
  readonly byStage: Record<ContentStage, number>;
}

/** Pure pipeline roll-up. "In progress" = everything not yet published. */
export function summarizeContent(items: readonly Pick<ContentItem, 'stage'>[]): ContentSummary {
  const byStage: Record<ContentStage, number> = { idea: 0, draft: 0, review: 0, scheduled: 0, published: 0 };
  let published = 0;
  for (const c of items) {
    byStage[c.stage] += 1;
    if (isPublished(c.stage)) published += 1;
  }
  return { total: items.length, published, inProgress: items.length - published, byStage };
}
